import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { StopCardList } from './StopCardList';
import { FilterContent } from './FilterContent';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_totalSavedCountState, atom_selectedFilterState } from '@app/shared/state/filter.atom';
import { atom_tourState, atom_originalTourState } from '@app/shared/state/tour.atom';
import { useRenderTracker } from '@app/shared/debug';

interface DesktopListContentProps {
  isHebrew: () => boolean;
  isListMode: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  preventMapInteraction: (e: React.MouseEvent | React.TouchEvent) => void;
  mapref?: React.RefObject<any>;
  flyToSelectedStop?: (stopId: string) => void;
}

export const DesktopListContent: React.FC<DesktopListContentProps> = ({
  isHebrew,
  isListMode,
  contentRef,
  preventMapInteraction,
  mapref,
  flyToSelectedStop
}) => {
  useRenderTracker('DesktopListContent');
  const { t } = useTranslation();

  // Import atoms directly
  const activeStopState = useRecoilValue(atom_activeStopState);
  const activeStopId = activeStopState?.id;
  const listMode = useRecoilValue(atom_listModeState);
  const sideBarState = useRecoilValue(sideBarStateAtom);
  const totalSavedCount = useRecoilValue(atom_totalSavedCountState);
  const selectedFilters = useRecoilValue(atom_selectedFilterState);
  const tourState = useRecoilValue(atom_tourState);
  const setSelectedFilters = useSetRecoilState(atom_selectedFilterState);
  const setActiveStopState = useSetRecoilState(atom_activeStopState);
  const setTourState = useSetRecoilState(atom_tourState);
  const originalTour = useRecoilValue(atom_originalTourState);

  // Applied filters count (not stops)
  const appliedFiltersCount = Array.isArray(selectedFilters)
    ? selectedFilters.filter(Boolean).length
    : 0;

  const showSavedBanner = sideBarState === 'saved';
  // Show filter banner only when filters are applied
  const showFilterBanner = appliedFiltersCount > 0;

  const clearFilters = useCallback(() => {
    setSelectedFilters([]);
    setActiveStopState(undefined);
    setTourState(originalTour);
  }, [setSelectedFilters, setActiveStopState, setTourState, originalTour]);

  // Refs to track scroll position and previous mode
  const savedScrollPositionRef = useRef<number>(0);
  const lastTrackedScrollRef = useRef<number>(0);
  const previousModeRef = useRef<string | null>(listMode);

  // Continuously track scroll position (throttled every 30px) when not in filter mode
  useEffect(() => {
    const scrollElement = contentRef.current;
    if (!scrollElement || listMode === 'filter') return;

    const handleScroll = () => {
      const currentScroll = scrollElement.scrollTop;
      // Only update if scrolled at least 30px from last tracked position
      if (Math.abs(currentScroll - lastTrackedScrollRef.current) >= 30) {
        lastTrackedScrollRef.current = currentScroll;
        savedScrollPositionRef.current = currentScroll;
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [contentRef, listMode]);

  // Restore scroll position when exiting filter mode
  useEffect(() => {
    const prevMode = previousModeRef.current;

    // Exiting filter mode - restore scroll position
    if (listMode !== 'filter' && prevMode === 'filter') {
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = savedScrollPositionRef.current;
        }
      }, 20);
    }

    previousModeRef.current = listMode;
  }, [listMode, contentRef]);

  // Scroll selected card into view (only when not in filter mode)
  useEffect(() => {
    if (listMode === 'filter') return;
    if (!activeStopId) return;
    if (!contentRef.current) return;

    // Check if scroll should be skipped (set by MapCard's skipScrollRef)
    const skipScroll = (window as any).__skipListScroll;
    if (skipScroll) {
      (window as any).__skipListScroll = false;
      return;
    }

    const cardNode = document.getElementById(activeStopId);
    if (cardNode && contentRef.current) {
      const container = contentRef.current;
      const cardTop = cardNode.offsetTop;
      const cardBottom = cardTop + cardNode.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;

      // Only scroll if the card is not fully visible
      if (cardTop < viewTop || cardBottom > viewBottom) {
        container.scrollTo({
          top: cardTop - 45,
          behavior: 'smooth',
        });
      }
    }
  }, [activeStopId, contentRef, listMode]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        pt: 1,
        position: 'relative',
        backgroundColor: tourState?.config?.listColor ? `#${tourState.config.listColor.replace('#', '')}` : '#F6F6F6',
      }}
      onClick={preventMapInteraction}
      onTouchStart={preventMapInteraction}
      onTouchMove={preventMapInteraction}
      onMouseDown={preventMapInteraction}
      onMouseMove={preventMapInteraction}
    >
      <Box
        component="nav"
        ref={contentRef}
        role="navigation"
        aria-label={t('accessibility.tour_stops_list') as string}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: /* 'calc(100vh - 120px)' */'100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#555'
            }
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
        }}
      >
        {(showSavedBanner || showFilterBanner) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              alignItems: 'stretch',
            }}
          >
            {showSavedBanner && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  width: '100%',
                }}
              >
                {(t('saved') || 'Saved') + (totalSavedCount ? ` (${totalSavedCount})` : '')}
              </Box>
            )}
            {showFilterBanner && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: 600,
                  gap: 1,
                  width: '100%',
                }}
              >
                {(() => {
                  const filterLabel = appliedFiltersCount === 1 && selectedFilters[0]
                    ? selectedFilters[0]
                    : (t('filter') || 'Filter');
                  return `${filterLabel}${appliedFiltersCount ? ` (${tourState?.stops.length})` : ''}`;
                })()}
                <Button
                  onClick={clearFilters}
                  disabled={appliedFiltersCount === 0}
                  variant="text"
                  disableRipple
                  sx={{
                    color: appliedFiltersCount > 0 ? '#48526B' : '#999',
                    backgroundColor: 'transparent',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    minWidth: 'auto',
                    p: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: appliedFiltersCount > 0 ? 'underline' : 'none',
                    },
                    '&.Mui-disabled': {
                      color: '#999',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  {t('clear_all') || 'Clear all'}
                </Button>
              </Box>
            )}
          </Box>
        )}
        {/* Show FilterContent when list mode is 'filter', otherwise show StopCardList */}
        {listMode === 'filter' ? (
          <FilterContent />
        ) : (
          <StopCardList
            mode="desktop"
            isHebrew={isHebrew}
            isListMode={isListMode}
            preventMapInteraction={preventMapInteraction}
            mapref={mapref}
            flyToSelectedStop={flyToSelectedStop}
          />
        )}
      </Box>
    </Box>
  );
};
